import React, { useEffect, useMemo, useState } from 'react'
import { BRAND_NAME } from '../../theme/brand';
import Header from '../../components/Header'
import {
  Flex, Box, Text, Center, Spinner, Link as ChakraLink, VStack, Button,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Tag,
} from '@chakra-ui/react';

import { LuCode2, LuSmartphone } from 'react-icons/lu'

import { fetchWrapper } from '../../helpers'
import PlatformDoc from './PlatformDoc/PlatformDoc'
import ConfigurePushNotification from '../configurePushNotification/ConfigurePushNotification'
import APNConfigurationPage from '../ConfigureApn/APN'
import { getPlatforms, getPlatform } from './SdkConfigApi'
import { DOC_TYPES, normalizePlatform } from './sdkDocTypes'
import { useSearchParams, Link } from 'react-router-dom';
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction'
import API from '../../helpers/axiosInstance'

/**
 * Full-page docs layout (Next.js docs style — nextjs.org/docs): a fixed-width
 * left nav sticks alongside the content instead of everything living inside a
 * narrow centered column. The nav groups platforms under their documentation
 * type (Event / Push / Stories) as nested links — the type itself isn't a
 * filter you have to pick before seeing platforms, it's just the section
 * header sitting above them, all visible at once like nextjs.org/docs' sidebar.
 */
const Configuresdk = ({ header }) => {
  const [data, setData] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()

  const [platforms, setPlatforms] = useState([])
  const [platformsLoading, setPlatformsLoading] = useState(true)

  const [selectedPlatformId, setSelectedPlatformId] = useState(searchParams.get('sdk') || null)
  // Which config page (if any) is showing in the main panel instead of a platform's
  // doc — set by the sidebar quick links below so they open inline, not a new page.
  const [specialView, setSpecialView] = useState(null)
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=documentation/configure-sdk`;
      const response = await API.get(url, { headers: getApiHeaders() });

      setPermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPermissions();
  }, []);

  const viewPermission = hasPermission("configure-sdk", permissions)
  useEffect(() => {
    loadKey();
  }, []);

  useEffect(() => {
    loadPlatforms();
  }, []);

  // Deep-linked `?sdk=` (or the browser back/forward buttons) takes priority.
  useEffect(() => {
    const sdk = searchParams.get('sdk')
    if (sdk && sdk !== selectedPlatformId) {
      setSelectedPlatformId(sdk)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Once platforms load, fall back to the first one if nothing valid is selected
  // (no `?sdk=` param, or it pointed at a platform that doesn't exist / is disabled).
  useEffect(() => {
    if (platformsLoading || platforms.length === 0) return
    const exists = platforms.some(p => p.id === selectedPlatformId)
    if (!exists) {
      const first = platforms[0].id
      setSelectedPlatformId(first)
      setSearchParams({ sdk: first })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platforms, platformsLoading])

  const loadKey = async () => {
    let user = JSON.parse(localStorage.getItem('customer'))

    const url = `${process.env.REACT_APP_API_URL}/customer/get-sdk-key`;
    const data = await fetchWrapper.get(url)

    setData(data)
  }

  // Lean list gives us which platforms are enabled/ordered; full detail (heading,
  // description, content, variant content) is fetched per platform since the list
  // response intentionally omits those larger fields.
  const loadPlatforms = async () => {
    setPlatformsLoading(true)
    try {
      const list = await getPlatforms()
      const enabled = (list || []).filter(p => p.enabled).sort((a, b) => a.order - b.order)
      const details = await Promise.all(enabled.map(p => getPlatform(p.id)))
      setPlatforms(details.filter(Boolean).map(normalizePlatform))
    } catch (error) {
      console.error('Error loading SDK platforms:', error)
      setPlatforms([])
    } finally {
      setPlatformsLoading(false)
    }
  }

  const handleSelectPlatform = (id) => {
    setSpecialView(null)
    setSelectedPlatformId(id)
    setSearchParams({ sdk: id })
  }

  // Group platforms under their documentation type for the nested sidebar nav.
  // Anything whose type doesn't match a known DOC_TYPE still needs a home, so
  // it lands in a catch-all "Other" group rather than silently disappearing.
  const groupedPlatforms = useMemo(() => {
    const groups = DOC_TYPES
      .map(t => ({ id: t.id, label: t.label, items: platforms.filter(p => Number(p.type_id) === t.id) }))
      .filter(g => g.items.length > 0)
    const groupedIds = new Set(groups.flatMap(g => g.items.map(p => p.id)))
    const ungrouped = platforms.filter(p => !groupedIds.has(p.id))
    if (ungrouped.length > 0) groups.push({ id: 'other', label: 'Other', items: ungrouped })
    return groups
  }, [platforms])

  const selectedPlatform = platforms.find(p => p.id === selectedPlatformId) || platforms[0]

  // Real-time config status for the two "jump straight to the config page" sidebar
  // links below — same fields ConfigBanner (in PlatformDoc) reads off /get-sdk-key.
  const androidPushConfigured = data?.android === 1
  const iosPushConfigured = !!data?.ios

  // Accordion sections default closed — except whichever one holds the platform
  // that's actually open, so landing on the page doesn't hide your own location.
  // `defaultIndex` is only read on mount, which is exactly when we want it: this
  // component doesn't render the Accordion at all until loading has finished, so
  // `groupedPlatforms`/`selectedPlatform` are already settled by then.
  const defaultOpenIndex = groupedPlatforms.findIndex(g => g.items.some(p => p.id === selectedPlatform?.id))

  if (loading || platformsLoading) {
    return (
      <>
        <Header />
        <Center h="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Center>
      </>
    );
  }

  if (!viewPermission) {
    return (
      <>
        <Header header={header} />
        <Center h="400px">
          <Box textAlign="center">
            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
              No Access
            </Text>
            <Text fontSize="sm" color="gray.500">
              You don't have permission to view this sections.
            </Text>
          </Box>
        </Center>
      </>
    );
  }

  return (
    <>
      <Header header={header} />
      {/* Same white-card-on-#f8fafc-page convention as the rest of the portal
          (`.box-style-card`, BoxWrapper's page background) — just laid out as
          two full-width panels side by side instead of one centered card. */}
      <Flex align="flex-start" gap={6} px={6} py={6}>
        {/* Left nav — full height, sticks as the page scrolls, same shape as
            nextjs.org/docs' sidebar: a page title up top, then every section
            (documentation type) with its pages (platforms) nested underneath. */}
        <Box
          className="box-style-card"
          bg="white"
          w={{ base: '100%', md: '260px' }}
          flexShrink={0}
          px={4}
          py={5}
          position={{ md: 'sticky' }}
          top="24px"
          alignSelf="flex-start"
          maxH={{ md: 'calc(100vh - 48px)' }}
          overflowY={{ md: 'auto' }}
        >
          <ChakraLink as={Link} to="/documentation/configure-sdk" fontSize="xs" color="gray.500">
            ← Back to SDK Integration
          </ChakraLink>

          <Flex align="center" mt={3} mb={1}>
            <LuSmartphone style={{ marginRight: '.5rem', color: 'var(--brand-primary)', flexShrink: 0 }} />
            <Text fontSize="lg" fontWeight="semibold">SDK Integration</Text>
          </Flex>
          <Text fontSize="xs" color="gray.500" mb={6}>
            Integrate the {BRAND_NAME} SDK into your mobile apps
          </Text>

          <Accordion allowMultiple defaultIndex={defaultOpenIndex !== -1 ? [defaultOpenIndex] : []} reduceMotion>
            {groupedPlatforms.map(group => (
              <AccordionItem key={group.id} border="none" mb={1}>
                <AccordionButton
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  _hover={{ bg: 'gray.100' }}
                >
                  <Text
                    flex="1"
                    textAlign="left"
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.600"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    {group.label}
                  </Text>
                  <Tag size="sm" mr={1} colorScheme="gray" fontSize="10px">
                    {group.items.length}
                  </Tag>
                  <AccordionIcon color="gray.500" />
                </AccordionButton>

                <AccordionPanel px={0} py={1}>
                  <VStack align="stretch" spacing={0.5}>
                    {/* Quick links — skip the SDK integration doc entirely and jump
                        straight to the actual config page, same as answering "is it
                        configured?" would: the dot mirrors ConfigBanner's status. */}
                    {group.id === 1 && (
                      <>
                        <Button
                          size="sm"
                          justifyContent="flex-start"
                          leftIcon={<Box w="7px" h="7px" borderRadius="full" bg={androidPushConfigured ? 'green.400' : 'orange.400'} flexShrink={0} />}
                          variant="ghost"
                          fontWeight={specialView === 'push' ? '600' : '500'}
                          color={specialView === 'push' ? 'var(--brand-primary)' : 'gray.700'}
                          bg={specialView === 'push' ? 'rgb(var(--brand-primary-rgb) / 0.08)' : 'transparent'}
                          _hover={{ bg: specialView === 'push' ? 'rgb(var(--brand-primary-rgb) / 0.12)' : 'gray.100' }}
                          borderRadius="md"
                          onClick={() => setSpecialView('push')}
                        >
                          Configure Push Notification
                        </Button>
                        <Button
                          size="sm"
                          justifyContent="flex-start"
                          leftIcon={<Box w="7px" h="7px" borderRadius="full" bg={iosPushConfigured ? 'green.400' : 'orange.400'} flexShrink={0} />}
                          variant="ghost"
                          fontWeight={specialView === 'apn' ? '600' : '500'}
                          color={specialView === 'apn' ? 'var(--brand-primary)' : 'gray.700'}
                          bg={specialView === 'apn' ? 'rgb(var(--brand-primary-rgb) / 0.08)' : 'transparent'}
                          _hover={{ bg: specialView === 'apn' ? 'rgb(var(--brand-primary-rgb) / 0.12)' : 'gray.100' }}
                          borderRadius="md"
                          onClick={() => setSpecialView('apn')}
                        >
                          Configure APN (iOS)
                        </Button>
                        <Box borderTopWidth="1px" borderColor="gray.100" my={1} />
                      </>
                    )}
                    {group.items.map(p => {
                      const isActive = selectedPlatform?.id === p.id
                      return (
                        <Button
                          key={p.id}
                          size="sm"
                          justifyContent="flex-start"
                          leftIcon={<LuCode2 size={14} />}
                          variant="ghost"
                          fontWeight={isActive ? '600' : '400'}
                          color={isActive ? 'var(--brand-primary)' : 'gray.700'}
                          bg={isActive ? 'rgb(var(--brand-primary-rgb) / 0.08)' : 'transparent'}
                          _hover={{ bg: isActive ? 'rgb(var(--brand-primary-rgb) / 0.12)' : 'gray.100' }}
                          borderRadius="md"
                          borderLeft="2px solid"
                          borderLeftColor={isActive ? 'var(--brand-primary)' : 'transparent'}
                          onClick={() => handleSelectPlatform(p.id)}
                        >
                          {p.label}
                        </Button>
                      )
                    })}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>

        {/* Content — fills the rest of the page (not squeezed into a centered
            column), but the text/code itself still caps at a readable width
            instead of stretching edge-to-edge on wide monitors. */}
        <Box className="box-style-card" bg="white" flex="1" minW="0" px={{ base: 4, md: 10 }} py={8}>
          {specialView === 'push' ? (
            <Box maxW="900px">
              <ConfigurePushNotification embedded />
            </Box>
          ) : specialView === 'apn' ? (
            <Box maxW="900px">
              <APNConfigurationPage embedded />
            </Box>
          ) : selectedPlatform ? (
            <Box maxW="900px">
              <PlatformDoc platform={selectedPlatform} />
            </Box>
          ) : (
            <Center h="200px">
              <Text color="gray.500">No SDK platforms are available right now.</Text>
            </Center>
          )}
        </Box>
      </Flex>
    </>
  )
}

export default Configuresdk
